import {
  Component, Inject, Input, OnDestroy, OnInit, ViewChild,
} from '@angular/core';
import {
  MAT_DIALOG_DATA, MatDialog, MatDialogRef,
} from '@angular/material/dialog';
import { Observable, Subscription } from 'rxjs';
import { CoursesService } from 'src/app/services/courses.service';
import { VerificationComponent } from 'src/app/components/verification/verification.component';
import { EntityService } from 'src/app/services/entity.service';
import { ToastService } from 'src/app/services/toast.service';
import { AuthService } from 'src/app/services/auth.service';
import { RequirementsService } from 'src/app/services/requirements.service';
import Course, { getSubmittableCourseRequirements, hasBlockedSchoolCounselorRequirementSelection, SCHOOL_COUNSELOR_REQUIREMENT_ERROR_TEXT } from './course.class';
import { CourseRequestsService } from 'src/app/services/course-requests.service';
import { CatalogSuggestionsService } from 'src/app/services/catalog-suggestions.service';
import { Router } from '@angular/router';
import { CourseRequestAttachmentsComponent } from 'src/app/routes/profile/components/my-course-requests/components/update-my-course-request/components/course-request-attachments/course-request-attachments.component';
import { PendingFileInfo } from 'src/app/components/upload-file/upload-file.component';
import { FinishComponent } from './components/finish/finish.component';

@Component({
  selector: 'app-update-course',
  templateUrl: './update-course.component.html',
  styleUrls: ['./update-course.component.scss'],
})
export class UpdateCourseComponent implements OnInit, OnDestroy {
  @Input() courseToEdit: any;
  @ViewChild(CourseRequestAttachmentsComponent, { static: false })
  attachmentsStep?: CourseRequestAttachmentsComponent;
  @ViewChild(FinishComponent, { static: false })
  finishComponent?: FinishComponent;
  courseToEditDetails: any;
  entitySubscription: any;
  requirementSubscription: Subscription;
  user$: Observable<any>;
  entity$: Observable<any>;

  steps = [
    { completed: false, text: 'General Info' },
    { completed: false, text: 'Course Details' },
    { completed: false, text: 'Course Sorting' },
    { completed: false, text: 'Additional Details' },
    { completed: false, text: 'Attachments' },
    { completed: false, text: 'Review & finish' },
  ]
  selectedStep = this.steps[0];
  originalCourse: Course;
  updatedCourse: Course;
  entity: any;
  requirements: any;
  requirementsList: any;
  tags: any;
  courseTypes: any;
  categories: any;
  courseSubscription: Subscription;
  updateCourseSubscription: Subscription;
  isLoading: boolean;
  isDuplicateCourse = false;
  previousYearRequirements: any;
  isEditMode = false;
  error: string;
  permissions: any;
  permissionsSubscription: Subscription;
  isCourseRequestApproval: boolean;
  isCatalogSuggestionsApproval: boolean;
  courseRequestApprovalUserId: any;
  catalogSuggestionUserId: any;
  catalogSuggestionCourseId: any;
  catalogSuggestionCourseItemId: any;
  fileTypeInS3 = 'COURSE_DOCUMENT' as const;
  approvalMessageBody = '';
  stateRequirementValidation: { isRequired: boolean; isValid: boolean } = { isRequired: false, isValid: true };
  // Tracks per-step validity for the add-course stepper flow
  private stepValidity: Record<string, boolean> = {
    'General Info': false,
    'Course Details': false,
    'Course Sorting': true, // validated dynamically in isNextStepDisabled()
    'Additional Details': true,
    Attachments: true,
    'Review & finish': true,
  };

  constructor(
    private entityService: EntityService,
    private toastService: ToastService,
    private coursesService: CoursesService,
    private authService: AuthService,
    private requirementsService: RequirementsService,
    private courseRequestsService: CourseRequestsService,
    private catalogSuggestionsService: CatalogSuggestionsService,
    public dialogRef: MatDialogRef<UpdateCourseComponent>,
    private router: Router,
    public dialog: MatDialog, @Inject(MAT_DIALOG_DATA) public data: any,
  ) { }

  ngOnInit(): void {
    this.user$ = this.authService.user$;
    this.entity$ = this.authService.entity$;
    // there is currently no included association for categories, nor a way to get list of categories, but we would want to do that here as well

    // get the active requirements for the course sorting step
    this.requirementsService.getRequirementsChildrenIncluded()

    this.requirementSubscription = this.requirementsService.requirementsData$.subscribe((requirementData: any) => {
      const activeRequirements = requirementData?.rows?.map((requirement: any) => {
        return {
          name: requirement.requirement,
          requirement: requirement.requirement,
          Id: requirement.requirementId,
          requirementId: requirement.requirementId,
          parentId: requirement.parentRequirementId,
          topMostParentId: requirement.topMostParentId,
          topMostParentName: requirement.topMostParentName,
          nodeType: requirement.nodeType,
          childCount: requirement.childCount,
          Requirement: requirement.Requirement, // Preserve the nested Requirement object
          children: requirement?.Children?.map((requirement: any) => {
            return {
              name: requirement.requirement,
              requirement: requirement.requirement,
              Id: requirement.requirementId,
              requirementId: requirement.requirementId,
              parentId: requirement.parentRequirementId,
              topMostParentId: requirement.topMostParentId,
              topMostParentName: requirement.topMostParentName,
              nodeType: requirement.nodeType,
              childCount: requirement.childCount,
              Requirement: requirement.Requirement, // Preserve the nested Requirement object
              children: requirement?.Children?.map((requirement: any) => {
                return {
                  name: requirement.requirement,
                  requirement: requirement.requirement,
                  Id: requirement.requirementId,
                  requirementId: requirement.requirementId,
                  parentId: requirement.parentRequirementId,
                  topMostParentId: requirement.topMostParentId,
                  topMostParentName: requirement.topMostParentName,
                  nodeType: requirement.nodeType,
                  childCount: requirement.childCount,
                  Requirement: requirement.Requirement, // Preserve the nested Requirement object
                  children: null,
                }
              }),
            }
          }),
        }
      })
      this.requirements = activeRequirements
    })


    this.entityService.getEntity('Tags, CourseTypes, Categories')
    this.entitySubscription = this.entityService.entityData$.subscribe((entityData: any) => {
      this.entity = entityData
      // get the active tags for the course sorting step
      const activeTags = entityData?.Tags?.filter((tag: any) => tag.activeFlag)
      this.tags = activeTags
      // get the active course types for the general info step
      const activeCourseTypes = entityData?.CourseTypes?.filter((courseType: any) => courseType.activeFlag)
      this.courseTypes = activeCourseTypes;
      // get the active categories for the course sorting step, currently waiting for endpoint to be updated
      const activeCategories = entityData?.Categories?.filter((category: any) => category.activeFlag)
      this.categories = activeCategories
    })

    // here we get the users permissions
    this.permissionsSubscription = this.authService.permissions$.subscribe((permissions: any) => {
      this.permissions = permissions
    })

    // if we are editing a course or approving a course request, we want to populate the form with the course data
    if (this.data) {
      this.isDuplicateCourse = this.data?.duplicateCourse ?? false;


      setTimeout(() => {
        this.isEditMode = true;
        this.isCourseRequestApproval = this.data.isCourseRequestApproval;
        this.isCatalogSuggestionsApproval = this.data.isCatalogSuggestionsApproval;
        this.isLoading = true;

        // Handle catalog suggestions approval
        if (this.isCatalogSuggestionsApproval) {
          console.log('Catalog suggestion data:', this.data);

          // Store the IDs we need for approval
          this.catalogSuggestionCourseId = this.data.id;
          this.catalogSuggestionUserId = this.data.SubmitUser?.id;

          const firstCourseItem = this.data.CourseItems?.[0] || {};
          this.catalogSuggestionCourseItemId = firstCourseItem.courseItemId || firstCourseItem.id;

          console.log('Fetching full catalog suggestion details...');

          // Fetch the full catalog suggestion with all details (similar to getCourseSession)
          this.catalogSuggestionsService.getCatalogSuggestion(this.catalogSuggestionCourseId)
            .subscribe({
              next: (response: any) => {
                console.log('Full catalog suggestion response:', response);

                const fullData = response.data || response;
                const courseItem = fullData.CourseItems?.[0] || {};

                // Remove problematic nested properties
                const {
                  CourseItems, SubmitUser, ...courseData
                } = fullData;

                // Build data structure matching getCourseSession format
                const catalogSuggestionData = {
                  ...courseItem,
                  courseItemId: this.catalogSuggestionCourseItemId,
                  Course: {
                    ...courseData,
                    courseId: fullData.id || fullData.courseId,
                    Tags: courseData.Tags || [],
                    Categories: courseData.Categories || [],
                    Requirements: courseData.Requirements || [],
                  },
                };

                console.log('Formatted catalog suggestion data:', catalogSuggestionData);

                try {
                  const timezone = this.entityService.getLocalTimezoneWithFallback();
                  this.originalCourse = new Course(catalogSuggestionData, undefined, timezone);
                  this.updatedCourse = new Course(catalogSuggestionData, this.originalCourse, timezone);
                  console.log('Course objects created successfully');
                  this.isLoading = false;
                } catch (error) {
                  console.error('Error creating Course objects:', error);
                  this.error = 'Failed to load catalog suggestion data';
                  this.isLoading = false;
                }
              },
              error: (error) => {
                console.error('Error fetching catalog suggestion:', error);
                this.error = 'Failed to load catalog suggestion';
                this.isLoading = false;
              },
            });

          return; // Skip the regular course fetch logic
        }

        // we call for the course session details here
        this.coursesService.clearCourseSessionData();
        this.coursesService.getCourseSession(this.data.courseItemId, { includedAssociations: 'AllCourseDetails' })
        this.courseSubscription = this?.coursesService?.courseSessionData$?.subscribe((courseSessionData: any) => {
          if (!courseSessionData) return;
          const tempTagsList = courseSessionData.Course.Tags.filter((tag: any) => tag.endYearId === null || tag.startYearId === this.entity?.gcCurrentYearId)
          const tempCategoriesList = courseSessionData.Course.Categories.filter((category: any) => category.endYearId === null || category.startYearId === this.entity?.gcCurrentYearId)
          this.previousYearRequirements = this.getPreviousRequirementDisplayList(courseSessionData.Course.Requirements);
          const tempCourseSessionData = {
            ...courseSessionData,
            Course: {
              startDate: null,
              endDate: null,
              activeFlag: 1,
              ...courseSessionData.Course,
              Tags: tempTagsList,
              Categories: tempCategoriesList,
              Requirements: [],
            } as Course,
            ...{
              dateOffered: null,
              startDate: null,
              endDate: null,
              regStartDate: null,
              regEndDate: null,
              sessionStartDate: null,
              sessionEndDate: null,
              activeFlag: 1,
            },
          }
          const timezone = this.entityService.getLocalTimezoneWithFallback();
          this.originalCourse = new Course(courseSessionData, undefined, timezone)
          this.updatedCourse = new Course(this.isDuplicateCourse ? tempCourseSessionData : courseSessionData, this.originalCourse, timezone)
          this.isLoading = false;
        })
      }, 0)
      this.courseRequestApprovalUserId = this.data.userId;
    } else {
      const timezone = this.entityService.getLocalTimezoneWithFallback();
      this.originalCourse = new Course(undefined, undefined, timezone);
      this.updatedCourse = new Course(undefined, undefined, timezone).initializeForCreate();
    }
  }

  getFormMode = () => {
    if (!this.isEditMode) return 'Add New';

    if (this.isDuplicateCourse) return 'Duplicate';

    if (this.isCatalogSuggestionsApproval) return 'Approve';

    return 'Edit';
  }

  closeDialog = () => {
    if (this.updatedCourse && this.updatedCourse.edited) {
      const confirmDialogRef = this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Are you sure?',
          text: 'Any unsaved changes will be lost',
          confirmButtonTitle: 'Yes, exit',
          cancelButtonTitle: 'No, go back',
        },
      })
      confirmDialogRef.afterClosed().subscribe((result: any) => {
        if (result === 'verified') {
          this.dialog.closeAll()
          this.coursesService.clearCourseSessionData();
        }
      })
    } else {
      this.dialog.closeAll()
      this.coursesService.clearCourseSessionData();
    }
  }

  stepBack = () => {
    const currentIndex = this.steps.findIndex((step: any) => step === this.selectedStep)
    this.selectedStep = this.steps[currentIndex - 1]
  }

  stepForward = (isValid = false) => {
    const currentIndex = this.steps.findIndex((step: any) => step.text === this.selectedStep.text);
    if (!isValid) {
      this.steps[currentIndex].completed = false;
      return;
    }

    this.steps[currentIndex].completed = true;

    let index = currentIndex + 1;
    if (index === this.steps.length) {
      index = currentIndex;
    }

    this.selectedStep = this.steps[index]
  }

  onStepValidationChange = (stepText: string, validation: { isValid: boolean }) => {
    if (!stepText) return;
    this.stepValidity[stepText] = !!validation?.isValid;
  }

  isNextStepDisabled = (): boolean => {
    if (this.isEditMode) return false;
    const stepText = this.selectedStep?.text;
    if (!stepText) return false;

    // General Info + Course Details: use emitted validation from the step components
    if (stepText === 'General Info' || stepText === 'Course Details') {
      return !this.stepValidity[stepText];
    }

    // Course Sorting: requirements are required; state requirement may be required depending on selection/data
    if (stepText === 'Course Sorting') {
      const hasRequirements = getSubmittableCourseRequirements(this.updatedCourse?.requirementsList ?? []).length > 0;
      if (!hasRequirements) return true;
      if (hasBlockedSchoolCounselorRequirementSelection(this.updatedCourse?.requirementsList ?? [])) return true;

      if (this.stateRequirementValidation.isRequired && !this.stateRequirementValidation.isValid) {
        return true;
      }

      return false;
    }

    // All other steps: currently no required fields that block step progression here
    return false;
  }

  // This is to save changes while in edit mode for a course request approval or editing a course
  saveChanges = () => {
    if (this.shouldBlockSaveForSchoolCounselorRequirement()) {
      this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Invalid requirement selection',
          text: SCHOOL_COUNSELOR_REQUIREMENT_ERROR_TEXT,
        },
      });
      return;
    }

    if (this.shouldBlockSaveForStateRequirement()) {
      this.dialog.open(VerificationComponent, {
        data: {
          type: 'alert',
          title: 'Required field',
          text: 'Please select a State Requirement Type when a requirement is set to Report to State.',
        },
      });
      return;
    }

    this.isLoading = true;

    // Handle catalog suggestions approval
    if (this.isCatalogSuggestionsApproval) {
      console.log('Approving catalog suggestion...');
      console.log('User ID:', this.catalogSuggestionUserId);
      console.log('Course ID:', this.catalogSuggestionCourseId);
      console.log('Course Item ID:', this.catalogSuggestionCourseItemId);

      const courseChanges = this.updatedCourse.buildUpdateRequest(this.originalCourse);
      console.log('Course changes:', courseChanges);

      this.updateCourseSubscription = this.catalogSuggestionsService.approveCatalogSuggestions(
        courseChanges,
        this.catalogSuggestionUserId,
        this.catalogSuggestionCourseId,
        this.catalogSuggestionCourseItemId,
        this.approvalMessageBody,
      ).subscribe({
        next: (response: any) => {
          this.toastService.setToast({
            text: 'Catalog suggestion successfully approved',
            type: 'success',
            icon: true,
          });
          this.isLoading = false;
          this.dialogRef.close('success');
        },
        error: (error) => {
          console.log('error', error);
          this.isLoading = false;
          this.dialog.open(VerificationComponent, {
            data: {
              type: 'alert',
              title: 'Oops, something went wrong',
              text: error.error.errMsg || error.error.message,
            },
          });
        },
      });
      return;
    }

    // this is where we make a call when we are approving a course request
    if (this.isCourseRequestApproval) {
      // make a new object called courseChanges that is the difference between this.updatedCourse and this.updatedCourse.originalCourse
      const courseChanges = this.updatedCourse.buildUpdateRequest(this.originalCourse);
      this.updateCourseSubscription = this.courseRequestsService.approveCourseRequest(
        courseChanges, this.courseRequestApprovalUserId, this.updatedCourse.courseId, this.updatedCourse.courseItemId,
      ).subscribe({
        next: (response: any) => {
          // refresh page
          this.toastService.setToast({
            text: 'Course request successfully approved', type: 'success', icon: true,
          })
          this.isLoading = false;
          this.dialogRef.close('success')
        },
        error: (error) => {
          this.isLoading = false;
          this.dialog.open(VerificationComponent, {
            data: {
              type: 'alert',
              title: 'Oops, something went wrong',
              text: error.error.errMsg || error.error.message,
            },
          })
        },
      })
    } else {
      if (this.permissions?.COURSE_EDIT === 0) {
        this.isLoading = false
        this.dialog.closeAll()
        this.dialog.open(VerificationComponent, {
          data: {
            type: 'alert',
            title: 'Oops, something went wrong',
            text: 'You do not have permission to edit courses',
          },
        })
        return
      }
      this.isLoading = true;

      if (this.isDuplicateCourse) {
        const createCourse = this.updatedCourse.buildCreateRequest(true);
        this.updateCourseSubscription = this.coursesService.addCourse(createCourse).subscribe({
          next: async (response: any) => {
            try {
              // Extract courseItemId from response
              let courseItemId: number | null = null;
              let courseId: number | null = null;

              // Try to extract from response data
              if (response?.data) {
                const courseData = response.data;
                if (courseData?.CourseItems && Array.isArray(courseData.CourseItems) && courseData.CourseItems.length > 0) {
                  courseItemId = Number(courseData.CourseItems[0].id);
                  courseId = Number(courseData.CourseItems[0].courseId || courseData.id);
                } else if (courseData?.courseItemId) {
                  courseItemId = Number(courseData.courseItemId);
                  courseId = Number(courseData.courseId || courseData.id);
                } else if (courseData?.id) {
                  // If only courseId is available, we might need to fetch course items
                  courseId = Number(courseData.id);
                }
              }

              // Check if there are pending documents to upload
              const hasPendingDocuments = this.attachmentsStep?.getPendingDocuments?.()?.length > 0;

              if (hasPendingDocuments && courseItemId && !isNaN(courseItemId) && courseItemId > 0) {
                // Has documents to upload and valid courseItemId
                try {
                  this.attachmentsStep?.setCourseIdsFromData?.(response.data || response);
                  await this.attachmentsStep?.uploadFilesForCourse?.(courseItemId);
                } catch (err) {
                  // Attachment upload failed - continue anyway
                } finally {
                  this.toastService.setToast({
                    text: 'Course successfully added', type: 'success', icon: true,
                  });
                  this.isLoading = false;
                  this.dialogRef.close(response);
                }
              } else {
                // No documents to upload or no valid courseItemId
                this.toastService.setToast({
                  text: 'Course successfully added', type: 'success', icon: true,
                });
                this.isLoading = false;
                this.dialogRef.close(response);
              }
            } catch (err) {
              this.toastService.setToast({
                text: 'Course successfully added', type: 'success', icon: true,
              });
              this.isLoading = false;
              this.dialogRef.close(response);
            }
          },
          error: (error) => {
            this.isLoading = false;
            this.dialog.open(VerificationComponent, {
              data: {
                type: 'alert',
                title: 'Oops, something went wrong',
                text: error.error.errMsg || error.error.message,
              },
            })
          },
        })
      }
      // this is where we make a call when we are editing
      if (this.isEditMode && !this.data?.duplicateCourse) {
        const updateRequestObject = this.updatedCourse.buildUpdateRequest(this.originalCourse);

        this.updateCourseSubscription = this.coursesService.updateCourse(updateRequestObject, this.updatedCourse.courseId).subscribe({
          next: (response: any) => {
            this.toastService.setToast({
              text: 'Course successfully updated', type: 'success', icon: true,
            })
            this.isLoading = false;
            this.previousYearRequirements = [];
            this.dialogRef.close(response)
          },
          error: (error) => {
            this.isLoading = false;
            this.dialog.open(VerificationComponent, {
              data: {
                type: 'alert',
                title: 'Oops, something went wrong',
                text: error.error.errMsg || error.error.message,
              },
            })
          },
        })
      }
    }

  }

  onCourseCreated = (response: any) => {
    // Finish component handles document uploads after successful course creation
    // This handler just closes the dialog after everything is complete
    this.dialogRef.close(response);
  }

  getPendingDocuments = (): PendingFileInfo[] => {
    return this.attachmentsStep?.getPendingDocuments() ?? [];
  }

  checkDisabled = () => {
    if (this.isEditMode) {
      return this.shouldBlockSaveForStateRequirement() || this.shouldBlockSaveForSchoolCounselorRequirement();
    }

    // For add course mode, check if all required steps are valid
    // If we've reached the finish step and all previous steps passed validation, enable the button
    const generalInfoValid = this.stepValidity['General Info'] === true;
    const courseDetailsValid = this.stepValidity['Course Details'] === true;
    const courseSortingValid = (
      getSubmittableCourseRequirements(this.updatedCourse?.requirementsList ?? []).length > 0 &&
      !hasBlockedSchoolCounselorRequirementSelection(this.updatedCourse?.requirementsList ?? []) &&
      (!this.stateRequirementValidation.isRequired || this.stateRequirementValidation.isValid)
    );

    // If we're on the finish step and all previous steps are valid, enable the button
    if (this.selectedStep?.text === 'Review & finish') {
      return !(generalInfoValid && courseDetailsValid && courseSortingValid);
    }

    return false;
  }

  onStateRequirementValidationChange = (validation: { isRequired: boolean; isValid: boolean }) => {
    this.stateRequirementValidation = validation;
  }

  /** When a report-to-state requirement is selected and state types exist, a state type must be chosen. */
  private shouldBlockSaveForStateRequirement(): boolean {
    return (
      this.stateRequirementValidation.isRequired &&
      !this.stateRequirementValidation.isValid
    );
  }

  private shouldBlockSaveForSchoolCounselorRequirement(): boolean {
    const requirementsList = this.updatedCourse?.requirementsList ?? [];
    return (
      hasBlockedSchoolCounselorRequirementSelection(requirementsList) ||
      getSubmittableCourseRequirements(requirementsList).length === 0
    );
  }

  private getPreviousRequirementDisplayList = (requirements: any[] = []): string[] => {
    const displayNames: string[] = [];
    const seenNames = new Set<string>();

    requirements.forEach((requirement: any) => {
      const parentName = (requirement?.topMostParentName || '').trim();
      const requirementName = (requirement?.requirement || '').trim();
      const nodeType = requirement?.nodeType;

      if (parentName && nodeType === 'GROUP_LEAF' && !seenNames.has(parentName)) {
        seenNames.add(parentName);
        displayNames.push(parentName);
      }

      if (requirementName && !seenNames.has(requirementName)) {
        seenNames.add(requirementName);
        displayNames.push(requirementName);
      }
    });

    return displayNames;
  }

  ngOnDestroy(): void {
    this.entitySubscription.unsubscribe();
    this?.courseSubscription?.unsubscribe();
    this?.updateCourseSubscription?.unsubscribe();
    this.permissionsSubscription?.unsubscribe();
    this.requirementSubscription?.unsubscribe();
  }

}
